import { useState, useEffect } from 'react';
import useTranslation from 'next-translate/useTranslation';
import { useDispatch, useSelector } from 'react-redux';
import { useCountdown } from 'usehooks-ts';
import UAParser from 'ua-parser-js';

import {
  actions as cabinetActions,
  selectors as cabinetSelectors,
  hooks as cabinetHooks,
} from '@/domains/cabinet';
import { langFormatter } from '@/utils/languageFormat';

const useOtpVerification = ({ isPopup }) => {
  const dispatch = useDispatch();
  const { phoneNumber, uuid } = useSelector(cabinetSelectors.selectCabinet);
  const [otpRequested, setOtpRequested] = useState(true);

  const parser = new UAParser();
  const result = parser.getResult();
  const [count, { startCountdown, resetCountdown }] = useCountdown({
    countStart: 180,
    intervalMs: 1000,
  });
  useEffect(() => {
    startCountdown();
  }, []);
  if (count === 0) {
    setOtpRequested(false);
    resetCountdown();
  }

  const { lang } = useTranslation();
  const formattedLang = langFormatter({ lang });
  const [wrongOtp, setWrongOtp] = useState(false);
  const [showError, setShowError] = useState(false);
  const [otpTrigger] = cabinetHooks.useOtpRequest();
  const [signUpTrigger, { isLoading }] = cabinetHooks.useSignUp();
  const [otpValue, setOtpValue] = useState(['', '', '', '']);
  const [getAccountsTrigger, { isFetching: accountsFetching }] =
    cabinetHooks.useGetAccounts();

  const popUpConfirmHandler = async (data) => {
    try {
      dispatch(cabinetActions.setAccessToken(data.accessToken));
      const response = await getAccountsTrigger({
        lang: formattedLang,
        accessToken: data.accessToken,
        oneTime: isPopup,
      }).unwrap();
      dispatch(cabinetActions.setPopUpConfirm(true));
      dispatch(cabinetActions.setPopupAccount(response[0]));
    } catch (error) {
      setShowError(true);
    }
  };
  const handleSubmitOtpForm = async (value) => {
    const requestBody = {
      uuid,
      code: value.join(''),
      msisdn: phoneNumber,
      isEdit: false,
      deviceType: 'WEB',
      firebaseInstallationId: null,
      deviceVendor: result?.device?.vendor,
      deviceModel: result?.device?.model ?? result?.os?.name,
      osName: result?.os?.name,
      osVersion: result?.os?.version,
      oneTime: isPopup,
    };
    try {
      const data = await signUpTrigger({
        data: requestBody,
      }).unwrap();
      if (isPopup) {
        popUpConfirmHandler(data);
      } else {
        sessionStorage.setItem('accessToken', data.accessToken);
        sessionStorage.setItem('refreshToken', data.refreshToken);
        dispatch(cabinetActions.prevStep());
        window?.location?.assign('/cabinet');
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: 'Cabinet_login',
        });
      }
    } catch (error) {
      setWrongOtp(true);
    }
  };

  const resendHandler = async () => {
    try {
      const data = await otpTrigger({ phoneNumber }).unwrap();
      dispatch(cabinetActions.setUuId(data.uuid));
    } catch (error) {
      setShowError(true);
    }
  };

  const handleOtpRequest = () => {
    setOtpRequested(true);
    startCountdown();
  };

  const checkArray = (value) => {
    return value.every((code) => code.trim() !== '');
  };

  const submitHandler = (e) => {
    e.preventDefault();
    if (!otpRequested) {
      handleOtpRequest();
      resendHandler();
    }
  };

  const handleChangeOtpInput = (value) => {
    setOtpValue(value);

    if (checkArray(value)) {
      handleSubmitOtpForm(value);
    }
  };
  const formatPhoneNumber = (phoneNumber) => {
    const numericPhoneNumber = phoneNumber.replace(/\D/g, '');
    const formattedPhoneNumber = `+${numericPhoneNumber.slice(
      0,
      3,
    )} ${numericPhoneNumber.slice(3, 5)} ${numericPhoneNumber.slice(
      5,
      8,
    )} ${numericPhoneNumber.slice(8, 10)} ${numericPhoneNumber.slice(10)}`;
    return formattedPhoneNumber;
  };

  return {
    isLoading,
    wrongOtp,
    showError,
    otpRequested,
    otpValue,
    submitHandler,
    handleChangeOtpInput,
    closeError: () => setShowError(false),
    formatPhoneNumber,
    count,
    startCountdown,
    accountsFetching,
  };
};

export default useOtpVerification;
